import { Link } from "react-router-dom";
import { T } from "@/lib/theme";
import { BRANDS } from "@/lib/brands";
import { ArrowUpRight } from "lucide-react";
import ConstellationClose from "@/components/ConstellationClose";

/**
 * Arrival — Theme 1 atmosphere with a vivid observatory hero, then a sequence
 * of structured content sections introducing Erenown as the central structure
 * behind a disciplined orbit. Closing section is "The Erenown Register".
 */
function CoordRule({ left, right }) {
  return (
    <div className="flex items-center gap-4 font-plexmono text-[10.5px] tracking-[0.22em] uppercase" style={{ color: T.muted }}>
      <span>{left}</span>
      <span className="flex-1 h-px" style={{ background: T.border }} />
      <span style={{ color: T.accent }}>{right}</span>
    </div>
  );
}

/** Vivid animated observatory used behind the Arrival hero. */
function ArrivalObservatory() {
  const SIZE = 920;
  const C = SIZE / 2;
  const rings = [
    { r: 420, dash: "1 26", cls: "spin-slower",   nodes: [] },
    { r: 340, dash: "2 14", cls: "spin-slow",     nodes: [12, 140, 260] },
    { r: 260, dash: "1 18", cls: "spin-slow-rev", nodes: [60, 220, 330] },
    { r: 180, dash: "2 8",  cls: "spin-slow",     nodes: [30, 180] },
    { r: 110, dash: "0",    cls: "",              nodes: [] },
  ];

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-full max-w-[1100px] h-auto">
        <defs>
          <radialGradient id="arrCore" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor={T.accent} stopOpacity="0.55" />
            <stop offset="100%" stopColor={T.accent} stopOpacity="0" />
          </radialGradient>
          <linearGradient id="arrSweep" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor={T.accent} stopOpacity="0" />
            <stop offset="100%" stopColor={T.accent} stopOpacity="0.45" />
          </linearGradient>
          <radialGradient id="arrPlanet" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor={T.accent} stopOpacity="1" />
            <stop offset="60%" stopColor={T.accent} stopOpacity="0.6" />
            <stop offset="100%" stopColor={T.accent} stopOpacity="0" />
          </radialGradient>
          <radialGradient id="arrWarm" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor={T.warm} stopOpacity="1" />
            <stop offset="60%" stopColor={T.warm} stopOpacity="0.5" />
            <stop offset="100%" stopColor={T.warm} stopOpacity="0" />
          </radialGradient>
        </defs>

        <g opacity="0.25">
          {[...Array(7)].map((_, i) => (
            <circle key={`g${i}`} cx={C} cy={C} r={80 + i * 70} fill="none" stroke={T.border} strokeWidth="1" />
          ))}
        </g>

        {rings.map((ring, idx) => (
          <g key={idx} className={ring.cls} style={{ transformOrigin: `${C}px ${C}px` }}>
            {ring.dash !== "0" && (
              <circle
                cx={C} cy={C} r={ring.r}
                fill="none"
                stroke={idx === 1 ? T.accent : T.border}
                strokeOpacity={idx === 1 ? 0.5 : 0.55}
                strokeDasharray={ring.dash}
              />
            )}
            {ring.nodes.map((angDeg, ni) => {
              const a = (angDeg * Math.PI) / 180;
              const x = C + Math.cos(a) * ring.r;
              const y = C + Math.sin(a) * ring.r;
              const isWarm = idx === 2 && ni === 0;
              const big = idx === 1 && ni === 0;
              return (
                <g key={ni}>
                  <circle cx={x} cy={y} r={big ? 28 : 16} fill={isWarm ? "url(#arrWarm)" : "url(#arrPlanet)"} />
                  <circle cx={x} cy={y} r={big ? 6 : 4} fill={isWarm ? T.warm : T.accent} />
                </g>
              );
            })}
          </g>
        ))}

        <g className="radar-sweep" style={{ transformOrigin: `${C}px ${C}px` }}>
          <path
            d={`M ${C} ${C} L ${C + 420} ${C} A 420 420 0 0 1 ${C + 297} ${C + 297} Z`}
            fill="url(#arrSweep)"
          />
        </g>

        <g opacity="0.25">
          <line x1="0" x2={SIZE} y1={C} y2={C} stroke={T.border} />
          <line x1={C} x2={C} y1="0" y2={SIZE} stroke={T.border} />
        </g>

        <circle cx={C} cy={C} r="160" fill="url(#arrCore)" />
      </svg>
    </div>
  );
}

export default function Arrival() {
  return (
    <div data-testid="arrival-page">
      {/* HERO — observatory atmosphere */}
      <section className="relative overflow-hidden" data-testid="arrival-hero" style={{ minHeight: "100vh", background: T.bg }}>
        <ArrivalObservatory />

        <div aria-hidden className="absolute inset-0 opacity-20 pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle at center, ${T.border}66 1px, transparent 1px)`,
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse 70% 70% at 50% 50%, black, transparent 82%)",
        }} />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-20 min-h-[100vh] flex flex-col">
          <CoordRule left="ARRIVAL · MMXX" right="51.4769°N · 0.0005°W · GMT" />

          <div className="mt-auto max-w-2xl reveal-up">
            <span className="font-plexmono text-[10.5px] tracking-[0.28em] uppercase" style={{ color: T.accent }}>
              ERENOWN · CENTRAL STRUCTURE
            </span>
            <h1 className="font-fraunces font-light text-4xl sm:text-5xl lg:text-[60px] leading-[1.05] tracking-[-0.015em] mt-6" style={{ fontVariationSettings: "'SOFT' 50", color: T.text }}>
              At the <span className="italic font-normal" style={{ color: T.accent }}>center</span> of a growing orbit.
            </h1>
            <p className="mt-6 font-intertight text-[15.5px] leading-[1.85]" style={{ color: T.textSoft }}>
              Erenown is the central structure behind a disciplined orbit of brands, systems, and cross-border
              solution lines — built to create operational clarity, institutional value, and long-term relevance
              in the digital age.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/orbit" data-testid="arrival-cta-orbit"
                className="group inline-flex items-center gap-3 px-7 py-4 font-plexmono text-[11px] tracking-[0.28em] uppercase"
                style={{ background: T.accent, color: T.bg }}>
                Enter the Orbit <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link to="/capabilities" data-testid="arrival-cta-capabilities"
                className="inline-flex items-center gap-3 px-7 py-4 border font-plexmono text-[11px] tracking-[0.28em] uppercase"
                style={{ borderColor: T.border, color: T.text }}>
                Explore Capabilities
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 breathe">
          <div className="font-plexmono text-[10px] tracking-[0.3em] uppercase" style={{ color: T.muted }}>Scroll — into the structure</div>
          <div className="h-6 w-px" style={{ background: T.border }} />
        </div>
      </section>

      {/* SECTION 2 — A CENTRAL STRUCTURE FOR THE DIGITAL AGE */}
      <section className="relative border-t" style={{ borderColor: T.border, background: T.surface }} data-testid="arrival-central-structure">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <CoordRule left="I" right="A central structure" />
            <div className="mt-6 font-plexmono text-[10.5px] tracking-[0.22em] uppercase leading-[1.9]" style={{ color: T.muted }}>
              Type · Central Structure<br />Status · Operating<br />Horizon · Long-term
            </div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-fraunces font-light text-3xl md:text-5xl leading-[1.12]" style={{ color: T.text, fontVariationSettings: "'SOFT' 50" }}>
              A central structure for the <span className="italic" style={{ color: T.accent }}>digital age</span>.
            </h2>
            <div className="mt-8 max-w-2xl space-y-6 font-intertight text-[16px] leading-[1.9]" style={{ color: T.textSoft }}>
              <p>
                Erenown was built around a simple belief: in a connected and fast-changing world, useful work
                should lead to durable value. We develop structures that help businesses launch, institutions
                operate, and opportunities move across borders with greater clarity, coordination, and purpose.
              </p>
              <p>
                At its core, Erenown is not a single product or a loose collection of services. It is a central
                structure — a steady point from which brands, operational lines, and future-facing systems can
                be developed, aligned, and expanded.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — A DISCIPLINED ORBIT (brand preview cards) */}
      <section className="relative border-t" style={{ borderColor: T.border }} data-testid="arrival-disciplined-orbit">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <CoordRule left="II" right="The Orbit · 05 brands" />
          <div className="mt-10 grid grid-cols-12 gap-10 items-start">
            <div className="col-span-12 lg:col-span-5">
              <h2 className="font-fraunces font-light text-4xl md:text-6xl leading-[1.05]" style={{ color: T.text }}>
                A disciplined <span className="italic" style={{ color: T.accent }}>orbit</span>.
              </h2>
              <p className="mt-6 max-w-md font-intertight text-[16px] leading-[1.85]" style={{ color: T.textSoft }}>
                Around Erenown sits a focused register of brands and solution lines. Each one holds a distinct
                role within the wider structure — from business launch and practical enablement to institutional
                systems, trade support, and digital interoperability.
              </p>
              <div className="mt-10">
                <Link to="/orbit" data-testid="arrival-orbit-link"
                  className="group inline-flex items-center gap-3 font-plexmono text-[11px] tracking-[0.28em] uppercase pb-1 border-b"
                  style={{ color: T.accent, borderColor: T.accent }}>
                  View the Orbit <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </div>

            <div className="col-span-12 lg:col-span-7">
              <ul className="grid grid-cols-1 gap-px" style={{ background: T.border }}>
                {BRANDS.map((b, i) => (
                  <li key={b.id} data-testid={`arrival-brand-${b.id}`}>
                    <Link
                      to={`/orbit?brand=${b.id}`}
                      className="group block p-6 md:p-7 transition-colors"
                      style={{ background: T.surface }}
                    >
                      <div className="flex items-baseline justify-between gap-6">
                        <span className="font-plexmono text-[10px] tracking-[0.28em] uppercase" style={{ color: T.muted }}>0{i + 1}</span>
                        <span className="flex-1 font-fraunces text-2xl md:text-[28px]" style={{ color: T.text, fontVariationSettings: "'SOFT' 40" }}>{b.name}</span>
                        <span className="hidden md:inline font-plexmono text-[10px] tracking-[0.24em] uppercase" style={{ color: T.accentDim }}>{b.tagline}</span>
                        <ArrowUpRight className="h-4 w-4 opacity-50 group-hover:opacity-100 transition-opacity" style={{ color: T.accent }} />
                      </div>
                      <p className="mt-3 max-w-2xl font-intertight text-[14.5px] leading-[1.85]" style={{ color: T.textSoft }}>
                        {b.description.split(".")[0] + "."}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — STRUCTURED, NOT SCATTERED */}
      <section className="relative border-t" style={{ borderColor: T.border, background: T.surface }} data-testid="arrival-structured">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <CoordRule left="III" right="Structure" />
          </div>
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-fraunces font-light text-3xl md:text-5xl leading-[1.12]" style={{ color: T.text, fontVariationSettings: "'SOFT' 50" }}>
              Structured, <span className="italic" style={{ color: T.accent }}>not scattered</span>.
            </h2>
            <div className="mt-8 max-w-2xl space-y-6 font-intertight text-[16px] leading-[1.9]" style={{ color: T.textSoft }}>
              <p>
                Erenown brings together business launch support, operational systems, institutional technologies,
                trade-oriented growth paths, and digital evolution under one coherent direction. The result is a
                more connected way to build, operate, and expand.
              </p>
              <p>
                Some lines serve immediate practical needs. Others prepare the ground for larger institutional,
                operational, and cross-border opportunities. Together, they form a structure designed for
                continuity, not noise.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5 — BUILT FOR SERIOUS WORK AND LONG-TERM RELEVANCE */}
      <section className="relative border-t" style={{ borderColor: T.border }} data-testid="arrival-engage">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-8">
            <CoordRule left="IV" right="Engage" />
            <h2 className="font-fraunces font-light text-4xl md:text-6xl mt-8 leading-[1.05]" style={{ color: T.text, fontVariationSettings: "'SOFT' 50" }}>
              Built for serious work and <span className="italic" style={{ color: T.accent }}>long-term relevance</span>.
            </h2>
            <p className="mt-8 max-w-2xl font-intertight text-[16px] leading-[1.9]" style={{ color: T.textSoft }}>
              Whether the need is launch, coordination, institutional capability, market entry, or digital
              evolution, Erenown offers a structured point of entry.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <Link to="/contact" data-testid="arrival-cta-button"
              className="group inline-flex items-center gap-3 px-8 py-4 font-plexmono text-[11px] tracking-[0.3em] uppercase"
              style={{ background: T.warm, color: T.bg }}>
              Start a Conversation <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CLOSING — Constellation map (Arrival page) */}
      <ConstellationClose />
    </div>
  );
}
